import {
  ArrowLeft,
  Banknote,
  Boxes,
  ClipboardList,
  FileText,
  HardHat,
  Plus,
  ReceiptText,
  Truck,
  Users,
  Wrench,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Modal } from "../../../components/Modal";
import { api, rows } from "../../../lib/api";
import type { ApiList } from "../../../lib/types";
import { btpPaths } from "../api";
import { useBtpCapabilities } from "../access";
import type {
  Chantier,
  ChantierOverview,
  ConsommationChantier,
  LigneBordereau,
  LigneSituation,
  LotChantier,
  RapportChantier,
  SituationTravaux,
  SousTraitance,
} from "../types";

type Tab = "overview" | "lots" | "terrain" | "situations" | "sous_traitance" | "ressources" | "documents";
type CreateKind = "lot" | "boq" | "rapport" | "situation" | "situation_line" | "subcontract" | "consumption" | null;

function formatMoney(value: unknown, currency: string) {
  return `${Number(value || 0).toLocaleString("fr-FR")} ${currency}`;
}

function Metric({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="rounded-xl bg-slate-50 p-4">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-2 text-lg font-black text-slate-900">{String(value ?? "—")}</div>
    </div>
  );
}

export function ChantierDetailPage() {
  const capabilities = useBtpCapabilities();
  const { chantierId } = useParams();
  const id = Number(chantierId);
  const [tab, setTab] = useState<Tab>("overview");
  const [createKind, setCreateKind] = useState<CreateKind>(null);
  const [chantier, setChantier] = useState<Chantier | null>(null);
  const money = (value: unknown) => formatMoney(value, chantier?.devise || "XOF");
  const [overview, setOverview] = useState<ChantierOverview | null>(null);
  const [lots, setLots] = useState<LotChantier[]>([]);
  const [boq, setBoq] = useState<LigneBordereau[]>([]);
  const [rapports, setRapports] = useState<RapportChantier[]>([]);
  const [situations, setSituations] = useState<SituationTravaux[]>([]);
  const [situationLines, setSituationLines] = useState<LigneSituation[]>([]);
  const [subs, setSubs] = useState<SousTraitance[]>([]);
  const [consumptions, setConsumptions] = useState<ConsommationChantier[]>([]);
  type Terrain = {taches: {id:number; titre:string; statut:string; date_echeance:string | null}[]; documents: {id:number; nom:string; present:boolean; valide:boolean; reference_externe:string}[]; checklists: {id:number; nom:string; items:{id:number; libelle:string; valeur:boolean | null}[]}[]; livrables: {id:number; nom:string; statut:string}[]; reception:{peut_receptionner:boolean; blocages:string[]}};
  const [terrain, setTerrain] = useState<Terrain>({taches:[],documents:[],checklists:[],livrables:[],reception:{peut_receptionner:false,blocages:[]}});
  const [error, setError] = useState("");
  const [qualityBusy, setQualityBusy] = useState(false);
  async function quality(body: unknown) {
    setQualityBusy(true);
    try { await api.post(`/api/v1/btp/chantiers/${id}/qualite/`,body); await load(); }
    catch (err) { setError(err instanceof Error ? err.message : "Validation impossible."); }
    finally { setQualityBusy(false); }
  }
  const [payment, setPayment] = useState<SituationTravaux | null>(null);
  const [accounts, setAccounts] = useState<{id: number; code: string; nom: string; devise_id: string}[]>([]);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentKey, setPaymentKey] = useState("");
  const [special, setSpecial] = useState<"ADVANCE" | "RETENTION" | null>(null);

  async function openSpecial(type: "ADVANCE" | "RETENTION") {
    try { setAccounts(await api.get("/api/v1/btp/comptes/")); setPaymentKey(crypto.randomUUID()); setSpecial(type); setError(""); }
    catch (err) { setError(err instanceof Error ? err.message : "Comptes indisponibles."); }
  }

  async function collectSpecial(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!special || paymentBusy) return;
    const f = new FormData(event.currentTarget); setPaymentBusy(true);
    try { await api.post(`/api/v1/btp/chantiers/${id}/encaissement-special/`, {type: special, montant_ht: f.get("montant"), compte_id: Number(f.get("compte")), reference: f.get("reference"), idempotency_key: paymentKey}); setSpecial(null); await load(); }
    catch (err) { setError(err instanceof Error ? err.message : "Encaissement impossible."); }
    finally { setPaymentBusy(false); }
  }

  async function openPayment(situation: SituationTravaux) {
    try {
      const available = await api.get<typeof accounts>("/api/v1/btp/comptes/");
      setAccounts(available);
      setPayment(available.length ? await api.post<SituationTravaux>(`/api/v1/btp/situations/${situation.id}/facturer/`, {}) : situation);
      setPaymentKey(crypto.randomUUID());
      setError("");
    } catch (err) { setError(err instanceof Error ? err.message : "Comptes indisponibles."); }
  }

  async function collect(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!payment || paymentBusy) return;
    const form = new FormData(event.currentTarget);
    setPaymentBusy(true);
    try {
      await api.post(`/api/v1/btp/situations/${payment.id}/encaisser/`, {compte_id: Number(form.get("compte")), montant: form.get("montant"), reference: form.get("reference"), idempotency_key: paymentKey});
      setPayment(null);
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : "Encaissement impossible."); }
    finally { setPaymentBusy(false); }
  }

  async function load() {
    try {
      const permitted = <T,>(allowed: boolean, path: string): Promise<ApiList<T>> =>
        allowed ? api.get<ApiList<T>>(path) : Promise.resolve([]);
      const [c, o, l, b, r, s, sl, st, co] = await Promise.all([
        api.get<Chantier>(btpPaths.chantier(id)),
        api.get<ChantierOverview>(btpPaths.overview(id)),
        api.get<ApiList<LotChantier>>(`${btpPaths.lots}?chantier=${id}`),
        permitted<LigneBordereau>(capabilities.dqe || capabilities.measure || capabilities.financial_read, `${btpPaths.bordereau}?lot__chantier=${id}`),
        permitted<RapportChantier>(capabilities.reports || capabilities.quality, `${btpPaths.rapports}?chantier=${id}`),
        permitted<SituationTravaux>(capabilities.financial_read || capabilities.measure, `${btpPaths.situations}?chantier=${id}`),
        permitted<LigneSituation>(capabilities.financial_read || capabilities.measure, `${btpPaths.lignesSituation}?situation__chantier=${id}`),
        permitted<SousTraitance>(capabilities.subcontract || capabilities.financial_read, `${btpPaths.sousTraitances}?chantier=${id}`),
        permitted<ConsommationChantier>(capabilities.consumption || capabilities.costs, `${btpPaths.consommations}?chantier=${id}`),
      ]);
      const lotRows = rows(l);
      const lotIds = new Set(lotRows.map((item) => item.id));
      const situationRows = rows(s);
      const situationIds = new Set(situationRows.map((item) => item.id));
      setChantier(c);
      setOverview(o);
      setLots(lotRows);
      setBoq(rows(b).filter((item) => lotIds.has(item.lot)));
      setRapports(rows(r));
      setSituations(situationRows);
      setSituationLines(rows(sl).filter((item) => situationIds.has(item.situation)));
      setSubs(rows(st));
      setConsumptions(rows(co));
      const operational = await api.get<Terrain>(`/api/v1/btp/chantiers/${id}/terrain/`);
      if (!Array.isArray(operational)) setTerrain(operational);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible.");
    }
  }

  useEffect(() => {
    if (Number.isFinite(id)) void load();
  }, [id]);

  const boqById = useMemo(() => new Map(boq.map((line) => [line.id, line])), [boq]);

  async function createRecord(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!createKind) return;
    const data = Object.fromEntries(new FormData(event.currentTarget).entries());
    try {
      if (createKind === "lot") {
        await api.post(btpPaths.lots, {
          chantier: id,
          code: data.code,
          nom: data.nom,
          description: data.description || "",
          ordre: Number(data.ordre || 0),
          statut: data.statut || "PLANIFIE",
          montant_contractuel: data.montant_contractuel || "0",
          budget_previsionnel: data.budget_previsionnel || "0",
          avancement: "0",
        });
      } else if (createKind === "boq") {
        await api.post(btpPaths.bordereau, {
          lot: Number(data.lot),
          code: data.code,
          designation: data.designation,
          unite: data.unite,
          quantite_contractuelle: data.quantite_contractuelle || "0",
          prix_unitaire: data.prix_unitaire || "0",
        });
      } else if (createKind === "rapport") {
        await api.post(btpPaths.rapports, {
          chantier: id,
          date: data.date,
          resume: data.resume,
          avancement: data.avancement || "0",
          meteo: data.meteo || "",
          effectif: Number(data.effectif || 0),
          heures_travail: data.heures_travail || "0",
          incidents: data.incidents || "",
          blocages: data.blocages || "",
          securite: data.securite || "",
        });
      } else if (createKind === "situation") {
        await api.post(btpPaths.situations, {
          chantier: id,
          numero: Number(data.numero),
          date_debut: data.date_debut,
          date_fin: data.date_fin,
          date_situation: data.date_situation,
          retenue_garantie_pct: data.retenue_garantie_pct || "0",
          avances_deduites: data.avances_deduites || "0",
          autres_deductions: data.autres_deductions || "0",
          commentaire: data.commentaire || "",
        });
      } else if (createKind === "situation_line") {
        await api.post(btpPaths.lignesSituation, {
          situation: Number(data.situation),
          ligne_bordereau: Number(data.ligne_bordereau),
          quantite_periode: data.quantite_periode || "0",
          commentaire: data.commentaire || "",
        });
      } else if (createKind === "subcontract") {
        await api.post(btpPaths.sousTraitances, {
          chantier: id,
          lot: data.lot ? Number(data.lot) : null,
          prestataire_reference: data.prestataire_reference,
          objet: data.objet,
          montant_contrat: data.montant_contrat || "0",
          montant_paye: data.montant_paye || "0",
          statut: data.statut || "PLANNED",
          avancement: data.avancement || "0",
        });
      } else if (createKind === "consumption") {
        await api.post(btpPaths.consommations, {
          chantier: id,
          lot: data.lot ? Number(data.lot) : null,
          date: data.date,
          type_ressource: data.type_ressource,
          reference_externe: data.reference_externe || "",
          designation: data.designation,
          unite: data.unite,
          quantite: data.quantite,
          ...(capabilities.costs ? { cout_unitaire: data.cout_unitaire || "0" } : {}),
          mouvement_stock_reference: data.mouvement_stock_reference || "",
          notes: data.notes || "",
        });
      }
      setCreateKind(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    }
  }

  async function transition(situation: SituationTravaux, action: "soumettre" | "approuver" | "rejeter" | "reouvrir" | "marquer-payee") {
    try {
      await api.post(`${btpPaths.situations}${situation.id}/${action}/`, {});
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Transition impossible.");
    }
  }

  if (!chantier) {
    return <div className="p-10 text-center text-slate-500">{error || "Chargement du chantier…"}</div>;
  }

  const tabs: { id: Tab; label: string; icon: typeof HardHat; visible?: boolean }[] = [
    { id: "overview", label: "Vue générale", icon: HardHat },
    { id: "lots", label: "Lots & DQE", icon: Boxes, visible: capabilities.dqe || capabilities.measure || capabilities.financial_read },
    { id: "terrain", label: "Terrain", icon: ClipboardList, visible: capabilities.reports || capabilities.quality },
    { id: "situations", label: "Situations", icon: ReceiptText, visible: capabilities.financial_read || capabilities.measure },
    { id: "sous_traitance", label: "Sous-traitance", icon: Users, visible: capabilities.subcontract || capabilities.financial_read },
    { id: "documents", label: "Documents & réception", icon: FileText, visible: capabilities.quality || capabilities.manage },
    { id: "ressources", label: "Matériaux & engins", icon: Truck, visible: capabilities.consumption || capabilities.costs },
  ];

  return (
    <div>
      <Link to="/btp/chantiers" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900">
        <ArrowLeft size={16} /> Chantiers
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-mono text-slate-400">{chantier.code}</div>
          <h1 className="mt-1 text-3xl font-black">{chantier.titre}</h1>
          <p className="mt-2 text-sm text-slate-500">
            {chantier.adresse || "Site non renseigné"} · {chantier.statut}
          </p>
        </div>
        {chantier.projet && capabilities.costs && (
          <Link to={`/projects/${chantier.projet}`} className="btn-secondary">
            <FileText size={16} /> Projet #{chantier.projet}
          </Link>
        )}
      </div>

      {error && <div className="mt-4 rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>}

      <div className="mt-6 flex gap-2 overflow-x-auto border-b border-slate-200 pb-2">
        {tabs.filter((item) => item.visible !== false).map(({ id: tabId, label, icon: Icon }) => (
          <button
            key={tabId}
            onClick={() => setTab(tabId)}
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${
              tab === tabId ? "bg-slate-950 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Metric label="Avancement" value={`${Number(overview?.avancement || 0)} %`} />
            {capabilities.financial_read && <Metric label="Montant contrat" value={money(overview?.montant_contrat)} />}
            {capabilities.financial_read && <Metric label="Travaux exécutés" value={money(overview?.execute_brut)} />}
            {capabilities.financial_read && <Metric label="Reste à exécuter" value={money(overview?.reste_a_executer)} />}
            {capabilities.financial_read && <Metric label="Encaissé TTC" value={money(overview?.encaisse_ttc ?? overview?.paye_net)} />}
            {capabilities.costs && <Metric label="Sous-traitance engagée" value={money(overview?.sous_traitance_engagee)} />}
            {capabilities.costs && <Metric label="Consommations" value={money(overview?.cout_consommations)} />}
            <Metric label="Rapports terrain" value={overview?.rapports ?? 0} />
            {capabilities.financial_read && ["avance_recue", "avance_amortie", "avance_restante", "retenue_cumulee", "retenue_liberee", "retenue_restante"].map(key => <Metric key={key} label={key.replaceAll("_", " ")} value={money((overview as unknown as Record<string,unknown>)?.[key])} />)}
          </div>
          {capabilities.pay && <div className="mt-4 flex flex-wrap gap-2"><button className="btn-secondary" onClick={() => void openSpecial("ADVANCE")}>Encaisser une avance</button><button className="btn-secondary" onClick={() => void openSpecial("RETENTION")}>Libérer une retenue reçue</button></div>}
          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <section className="panel p-5">
              <h2 className="flex items-center gap-2 text-lg font-bold"><Banknote size={18}/> Contrat</h2>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-4"><span className="text-slate-500">Client</span><strong>{chantier.client_reference || "—"}</strong></div>
                <div className="flex justify-between gap-4"><span className="text-slate-500">Contrat</span><strong>{chantier.contrat_reference || "—"}</strong></div>
                {capabilities.costs && <div className="flex justify-between gap-4"><span className="text-slate-500">Budget prévisionnel</span><strong>{money(chantier.budget_previsionnel)}</strong></div>}
              </div>
            </section>
            <section className="panel p-5">
              <h2 className="flex items-center gap-2 text-lg font-bold"><Wrench size={18}/> Exécution BTP</h2>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <Metric label="Lots" value={overview?.lots ?? lots.length} />
                <Metric label="Situations" value={overview?.situations ?? situations.length} />
                <Metric label="DQE lignes" value={boq.length} />
              </div>
            </section>
          </div>
        </div>
      )}

      {tab === "lots" && (
        <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_1.35fr]">
          <section className="panel overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 p-5">
              <h2 className="font-bold">Lots de travaux</h2>
              {capabilities.manage && <button className="btn-primary" onClick={() => setCreateKind("lot")}><Plus size={16}/> Lot</button>}
            </div>
            <div className="divide-y divide-slate-100">
              {lots.map((lot) => (
                <div key={lot.id} className="p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div><strong>{lot.code}</strong><div className="text-sm text-slate-500">{lot.nom}</div></div>
                    <span className="badge bg-slate-100 text-slate-700">{lot.statut}</span>
                  </div>
                  {capabilities.manage && lot.statut !== "TERMINE" && <button className="btn-secondary mt-3" onClick={() => void (async () => {try {await api.patch(`${btpPaths.lots}${lot.id}/`,{statut:"TERMINE",avancement:"100"});await load();}catch(e){setError(e instanceof Error ? e.message : "Impossible.");}})()}>Terminer ce lot</button>}
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <div>Avancement <strong>{Number(lot.avancement)}%</strong></div>
                    {capabilities.costs && <div>Contrat <strong>{money(lot.montant_contractuel)}</strong></div>}
                    {capabilities.costs && <div>Budget <strong>{money(lot.budget_previsionnel)}</strong></div>}
                  </div>
                </div>
              ))}
              {!lots.length && <div className="p-8 text-center text-sm text-slate-500">Aucun lot.</div>}
            </div>
          </section>

          <section className="panel overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 p-5">
              <div><h2 className="font-bold">Bordereau / DQE</h2><p className="mt-1 text-xs text-slate-500">Quantités contractuelles × prix unitaires.</p></div>
              {capabilities.dqe && !chantier.baseline_figee && <button disabled={!lots.length} className="btn-primary" onClick={() => setCreateKind("boq")}><Plus size={16}/> Ligne</button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Lot</th><th className="px-4 py-3">Désignation</th><th className="px-4 py-3">Qté</th>{capabilities.financial_read && <th className="px-4 py-3">PU</th>}{capabilities.financial_read && <th className="px-4 py-3">Montant</th>}</tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {boq.map((line) => (
                    <tr key={line.id}>
                      <td className="px-4 py-3 font-semibold">{lots.find((lot) => lot.id === line.lot)?.code || line.lot}</td>
                      <td className="px-4 py-3"><strong>{line.code}</strong><div className="text-xs text-slate-500">{line.designation}</div></td>
                      <td className="px-4 py-3">{line.quantite_contractuelle} {line.unite}</td>
                      {capabilities.financial_read && <td className="px-4 py-3">{money(line.prix_unitaire)}</td>}
                      {capabilities.financial_read && <td className="px-4 py-3 font-bold">{money(line.montant_contractuel)}</td>}
                    </tr>
                  ))}
                  {!boq.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Aucune ligne DQE.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {tab === "terrain" && <section className="panel mt-5 p-5"><h2 className="font-bold">Tâches du chantier</h2>{terrain.taches.map(task => <div key={task.id} className="mt-3 flex justify-between gap-3 text-sm"><span>{task.titre}</span><span>{task.statut} {task.date_echeance || ""}</span></div>)}{!terrain.taches.length && <p className="mt-3 text-sm text-slate-500">Aucune tâche planifiée.</p>}</section>}
      {tab === "terrain" && (
        <section className="panel mt-6 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 p-5">
            <div><h2 className="font-bold">Rapports de chantier</h2><p className="mt-1 text-xs text-slate-500">Journal terrain : production, effectif, sécurité et blocages.</p></div>
            {capabilities.reports && <button className="btn-primary" onClick={() => setCreateKind("rapport")}><Plus size={16}/> Rapport</button>}
          </div>
          <div className="divide-y divide-slate-100">
            {rapports.map((rapport) => (
              <article key={rapport.id} className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div><strong>{rapport.date}</strong><span className="ml-3 badge bg-emerald-50 text-emerald-700">{rapport.avancement}%</span></div>
                  <div className="text-xs text-slate-500">{rapport.effectif} pers. · {rapport.heures_travail} h · {rapport.meteo || "météo n/a"}</div>
                </div>
                <p className="mt-3 text-sm text-slate-700">{rapport.resume}</p>
                {(rapport.incidents || rapport.blocages) && (
                  <div className="mt-3 grid gap-2 text-xs md:grid-cols-2">
                    <div className="rounded-lg bg-red-50 p-3"><strong>Incidents :</strong> {rapport.incidents || "Aucun"}</div>
                    <div className="rounded-lg bg-amber-50 p-3"><strong>Blocages :</strong> {rapport.blocages || "Aucun"}</div>
                  </div>
                )}
              </article>
            ))}
            {!rapports.length && <div className="p-10 text-center text-sm text-slate-500">Aucun rapport terrain.</div>}
          </div>
        </section>
      )}

      {tab === "situations" && (
        <div className="mt-6">
          <div className="flex flex-wrap justify-end gap-2">
            {capabilities.measure && <button className="btn-secondary" disabled={!boq.length || !situations.length} onClick={() => setCreateKind("situation_line")}><Plus size={16}/> Ajouter un métré</button>}
            {capabilities.finance && <button className="btn-primary" onClick={() => setCreateKind("situation")}><Plus size={16}/> Nouvelle situation</button>}
          </div>
          <div className="mt-4 space-y-4">
            {situations.map((situation) => {
              const lines = situationLines.filter((line) => line.situation === situation.id);
              return (
                <section key={situation.id} className="panel p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div><div className="text-xs font-semibold text-slate-400">Situation #{situation.numero}</div><h2 className="mt-1 text-lg font-bold">{situation.date_situation}</h2><div className="mt-1 text-sm text-slate-500">{situation.date_debut} → {situation.date_fin}</div></div>
                    <span className="badge bg-slate-100 text-slate-700">{situation.statut}</span>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {capabilities.financial_read && <Metric label="Brut" value={money(situation.montant_brut)} />}
                    {capabilities.financial_read && <Metric label="Retenue" value={money(situation.retenue_garantie)} />}
                    {capabilities.financial_read && <Metric label="Net" value={money(situation.montant_net)} />}
                  </div>
                  {capabilities.financial_read && situation.facture && <div className="mt-4 rounded-lg bg-blue-50 p-3 text-sm">Facture {situation.facture.numero} · TTC {money(situation.facture.total_ttc)} · Reçu {money(situation.facture.montant_regle)} · Reste {money(situation.facture.reste_a_payer)}</div>}
                  <div className="mt-4 space-y-2">
                    {lines.map((line) => {
                      const boqLine = boqById.get(line.ligne_bordereau);
                      return <div key={line.id} className="flex flex-wrap justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm"><span>{boqLine?.code || line.ligne_bordereau} · {boqLine?.designation}</span><strong>{capabilities.financial_read ? `${line.quantite_periode} × ${money(line.prix_unitaire_snapshot)} = ${money(line.montant)}` : `${line.quantite_periode} ${boqLine?.unite || ""}`}</strong></div>;
                    })}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {capabilities.finance && situation.statut === "DRAFT" && <button className="btn-primary" onClick={() => void transition(situation, "soumettre")}>Soumettre</button>}
                    {capabilities.approve && situation.statut === "SUBMITTED" && <>
                      <button className="btn-primary" onClick={() => void transition(situation, "approuver")}>Approuver</button>
                      <button className="btn-secondary" onClick={() => void transition(situation, "rejeter")}>Rejeter</button>
                    </>}
                    {capabilities.finance && situation.statut === "REJECTED" && <button className="btn-secondary" onClick={() => void transition(situation, "reouvrir")}>Réouvrir</button>}
                    {capabilities.pay && situation.statut === "APPROVED" && <button className="btn-primary" onClick={() => void openPayment(situation)}>Enregistrer un encaissement</button>}
                  </div>
                </section>
              );
            })}
            {!situations.length && <div className="panel p-10 text-center text-sm text-slate-500">Aucune situation de travaux.</div>}
          </div>
        </div>
      )}

      {tab === "sous_traitance" && (
        <section className="panel mt-6 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 p-5">
            <h2 className="font-bold">Sous-traitance</h2>
            {capabilities.subcontract && <button className="btn-primary" onClick={() => setCreateKind("subcontract")}><Plus size={16}/> Contrat</button>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Prestataire</th><th className="px-4 py-3">Objet</th>{capabilities.financial_read && <th className="px-4 py-3">Contrat</th>}{capabilities.financial_read && <th className="px-4 py-3">Payé</th>}<th className="px-4 py-3">Avancement</th><th className="px-4 py-3">Statut</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {subs.map((sub) => <tr key={sub.id}><td className="px-4 py-3 font-semibold">{sub.prestataire_reference}</td><td className="px-4 py-3">{sub.objet}</td>{capabilities.financial_read && <td className="px-4 py-3">{money(sub.montant_contrat)}</td>}{capabilities.financial_read && <td className="px-4 py-3">{money(sub.montant_paye)}</td>}<td className="px-4 py-3">{sub.avancement}%</td><td className="px-4 py-3">{sub.statut}</td></tr>)}
                {!subs.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Aucune sous-traitance.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "ressources" && (
        <section className="panel mt-6 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 p-5">
            <div><h2 className="font-bold">Matériaux, engins & consommations</h2><p className="mt-1 text-xs text-slate-500">Le champ mouvement stock permet le rapprochement avec le module Stocks.</p></div>
            {capabilities.consumption && <button className="btn-primary" onClick={() => setCreateKind("consumption")}><Plus size={16}/> Consommation</button>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Ressource</th><th className="px-4 py-3">Quantité</th>{capabilities.costs && <th className="px-4 py-3">Coût</th>}<th className="px-4 py-3">Réf. stock</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {consumptions.map((item) => <tr key={item.id}><td className="px-4 py-3">{item.date}</td><td className="px-4 py-3">{item.type_ressource}</td><td className="px-4 py-3 font-semibold">{item.designation}</td><td className="px-4 py-3">{item.quantite} {item.unite}</td>{capabilities.costs && <td className="px-4 py-3">{money(item.cout_total)}</td>}<td className="px-4 py-3 text-slate-500">{item.mouvement_stock_reference || "—"}</td></tr>)}
                {!consumptions.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Aucune consommation enregistrée.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "documents" && <div className="mt-6 grid gap-5">
        <section className="panel p-5"><h2 className="font-bold">Documents du projet</h2>{terrain.documents.map(doc => <form key={doc.id} className="mt-4 flex flex-wrap items-center gap-3" onSubmit={e => {e.preventDefault();const f = new FormData(e.currentTarget);void quality({document:doc.id,reference:f.get("reference")});}}><strong className="text-sm">{doc.nom}</strong><span className="text-xs">{doc.present && doc.valide ? "Validé" : "À fournir / valider"}</span>{capabilities.quality && !["RECEPTIONNE","ARCHIVE"].includes(chantier.statut) && <><input className="field max-w-sm" name="reference" aria-label={`Référence ${doc.nom}`} defaultValue={doc.reference_externe} placeholder="Référence de la pièce justificative" required maxLength={255}/><button className="btn-secondary" disabled={qualityBusy}>Valider la pièce</button></>}</form>)}{!terrain.documents.length && <p className="mt-3 text-sm text-slate-500">Aucun document partagé avec ce profil.</p>}</section>
        <section className="panel p-5"><h2 className="font-bold">Contrôles qualité et sécurité</h2>{terrain.checklists.map(checklist => <div key={checklist.id} className="mt-4"><h3 className="font-bold">{checklist.nom}</h3>{checklist.items.map(item => <label key={item.id} className="mt-3 flex items-center gap-3 text-sm"><input type="checkbox" checked={item.valeur === true} disabled={!capabilities.quality || qualityBusy || ["RECEPTIONNE","ARCHIVE"].includes(chantier.statut)} onChange={e => void quality({item:item.id,valeur:e.target.checked})}/>{item.libelle}</label>)}</div>)}</section>
        <section className="panel p-5"><h2 className="font-bold">DOE et réception</h2>{terrain.livrables.map(delivery => <div key={delivery.id} className="mt-3 flex flex-wrap items-center gap-3"><span>{delivery.nom} · {delivery.statut}</span>{capabilities.quality && !["RECEPTIONNE","ARCHIVE"].includes(chantier.statut) && delivery.statut !== "VALIDATED" && <button className="btn-secondary" disabled={qualityBusy} onClick={() => void quality({livrable:delivery.id})}>Valider le DOE</button>}</div>)}{terrain.reception.blocages.map((blocker,index) => <p className="mt-2 text-sm text-amber-700" key={index}>{blocker}</p>)}{capabilities.manage && chantier.statut !== "RECEPTIONNE" && <div className="mt-4 flex flex-wrap gap-3"><button className="btn-secondary" disabled={qualityBusy} onClick={() => void (async () => {try {await api.patch(btpPaths.chantier(id),{statut:"TERMINE"});await load();}catch(e){setError(e instanceof Error ? e.message : "Impossible.");}})()}>Terminer les travaux</button><button className="btn-primary" disabled={!terrain.reception.peut_receptionner || qualityBusy} onClick={() => void (async () => {try {await api.post(`/api/v1/btp/chantiers/${id}/receptionner/`,{});await load();}catch(e){setError(e instanceof Error ? e.message : "Réception impossible.");}})()}>Réceptionner le chantier</button></div>}</section>
      </div>}
      <Modal open={Boolean(special)} title={special === "ADVANCE" ? "Avance de démarrage reçue" : "Retenue de garantie reçue"} onClose={() => { if (!paymentBusy) setSpecial(null); }}>
        <form onSubmit={collectSpecial} className="grid gap-4">
          {error && <div role="alert" className="text-red-700">{error}</div>}
          <Select name="compte" label="Compte d’encaissement" options={accounts.map(account => [String(account.id), `${account.code} · ${account.nom}`])}/>
          <Field name="montant" label="Montant hors taxes reçu" type="number" min="0.01" step="0.01" required/>
          <Field name="reference" label="Référence du reçu" required/>
          <p className="text-sm text-slate-500">La facture applique la taxe d’acompte ou de retenue configurée par l’entreprise, puis enregistre le reçu sur le compte.</p>
          <button className="btn-primary" disabled={paymentBusy || !accounts.length}>Confirmer l’encaissement</button>
        </form>
      </Modal>
      <Modal open={Boolean(payment)} title="Encaissement de situation" onClose={() => { if (!paymentBusy) setPayment(null); }}>
        <form onSubmit={collect} className="grid gap-4">
          {error && <div role="alert" className="text-sm text-red-700">{error}</div>}
          <Select name="compte" label="Compte d’encaissement" options={accounts.map(account => [String(account.id), `${account.code} · ${account.nom} (${account.devise_id})`])}/>
          <Field name="montant" label="Montant reçu TTC" type="number" min="0.01" step="0.01" required defaultValue={payment ? Number(payment.facture?.reste_a_payer || payment.montant_net || 0).toFixed(2) : ""}/>
          <Field name="reference" label="Référence du reçu / virement" required/>
          <p className="text-sm text-slate-500">Le paiement sera enregistré dans le compte et affecté à la facture. Un paiement partiel laisse la situation approuvée.</p>
          <button className="btn-primary" disabled={paymentBusy || !accounts.length}>{paymentBusy ? "Enregistrement…" : "Confirmer l’encaissement"}</button>
          {!accounts.length && <p role="alert" className="text-sm text-amber-700">Aucun compte actif disponible. Créez un compte dans Comptes avant l’encaissement.</p>}
        </form>
      </Modal>
      <Modal open={Boolean(createKind)} title={
        createKind === "lot" ? "Nouveau lot" :
        createKind === "boq" ? "Nouvelle ligne DQE" :
        createKind === "rapport" ? "Rapport de chantier" :
        createKind === "situation" ? "Nouvelle situation" :
        createKind === "situation_line" ? "Ajouter un métré à une situation" :
        createKind === "subcontract" ? "Nouvelle sous-traitance" :
        "Nouvelle consommation"
      } onClose={() => setCreateKind(null)} wide>
        <form onSubmit={createRecord} className="grid gap-4 md:grid-cols-2">
          {createKind === "lot" && <>
            <Field name="code" label="Code du lot" required />
            <Field name="nom" label="Nom du lot" required />
            <Field name="ordre" label="Ordre" type="number" defaultValue="0" />
            <Select name="statut" label="Statut" options={[["PLANIFIE","Planifié"],["ACTIF","Actif"],["SUSPENDU","Suspendu"],["TERMINE","Terminé"]]} />
            <Field name="montant_contractuel" label="Montant contractuel" type="number" defaultValue="0" />
            <Field name="budget_previsionnel" label="Budget prévisionnel" type="number" defaultValue="0" />
            <Area name="description" label="Description" />
          </>}
          {createKind === "boq" && <>
            <label><span className="label">Lot</span><select className="field" name="lot" required>{lots.map((lot)=><option key={lot.id} value={lot.id}>{lot.code} · {lot.nom}</option>)}</select></label>
            <Field name="code" label="Code ligne" required />
            <Field name="designation" label="Désignation" required />
            <Field name="unite" label="Unité" required placeholder="m², m³, ml, u…" />
            <Field name="quantite_contractuelle" label="Quantité contractuelle" type="number" step="0.0001" defaultValue="0" />
            <Field name="prix_unitaire" label="Prix unitaire" type="number" defaultValue="0" />
          </>}
          {createKind === "rapport" && <>
            <Field name="date" label="Date" type="date" required />
            <Field name="avancement" label="Avancement (%)" type="number" min="0" max="100" defaultValue="0" />
            <Field name="meteo" label="Météo" />
            <Field name="effectif" label="Effectif" type="number" min="0" defaultValue="0" />
            <Field name="heures_travail" label="Heures de travail" type="number" min="0" step="0.01" defaultValue="0" />
            <Area name="resume" label="Travaux réalisés / résumé" required />
            <Area name="incidents" label="Incidents" />
            <Area name="blocages" label="Blocages" />
            <Area name="securite" label="Sécurité / QHSE" />
          </>}
          {createKind === "situation" && <>
            <Field name="numero" label="Numéro" type="number" min="1" required />
            <Field name="date_situation" label="Date de situation" type="date" required />
            <Field name="date_debut" label="Début de période" type="date" required />
            <Field name="date_fin" label="Fin de période" type="date" required />
            <Field name="retenue_garantie_pct" label="Retenue garantie (%)" type="number" min="0" max="100" defaultValue="0" />
            <Field name="avances_deduites" label="Avances déduites" type="number" min="0" defaultValue="0" />
            <Field name="autres_deductions" label="Autres déductions" type="number" min="0" defaultValue="0" />
            <Area name="commentaire" label="Commentaire" />
          </>}
          {createKind === "situation_line" && <>
            <label><span className="label">Situation</span><select className="field" name="situation" required>{situations.filter((s)=>["DRAFT","REJECTED"].includes(s.statut)).map((s)=><option key={s.id} value={s.id}>Situation #{s.numero} · {s.date_situation}</option>)}</select></label>
            <label><span className="label">Ligne DQE</span><select className="field" name="ligne_bordereau" required>{boq.map((line)=><option key={line.id} value={line.id}>{line.code} · {line.designation}</option>)}</select></label>
            <Field name="quantite_periode" label="Quantité exécutée sur la période" type="number" min="0" step="0.0001" required />
            <Area name="commentaire" label="Commentaire" />
          </>}
          {createKind === "subcontract" && <>
            <Field name="prestataire_reference" label="Prestataire / référence tiers" required />
            <Field name="objet" label="Objet" required />
            <label><span className="label">Lot</span><select className="field" name="lot"><option value="">Global chantier</option>{lots.map((lot)=><option key={lot.id} value={lot.id}>{lot.code} · {lot.nom}</option>)}</select></label>
            <Select name="statut" label="Statut" options={[["PLANNED","Prévue"],["ACTIVE","Active"],["SUSPENDED","Suspendue"],["COMPLETED","Terminée"]]} />
            <Field name="montant_contrat" label="Montant contrat" type="number" min="0" defaultValue="0" />
            <Field name="montant_paye" label="Montant payé" type="number" min="0" defaultValue="0" />
            <Field name="avancement" label="Avancement (%)" type="number" min="0" max="100" defaultValue="0" />
          </>}
          {createKind === "consumption" && <>
            <Field name="date" label="Date" type="date" required />
            <Select name="type_ressource" label="Type" options={[["MATERIAL","Matériau"],["EQUIPMENT","Engin / équipement"],["FUEL","Carburant"],["SERVICE","Service"],["OTHER","Autre"]]} />
            <Field name="designation" label="Désignation" required />
            <Field name="reference_externe" label="Référence externe" />
            <label><span className="label">Lot</span><select className="field" name="lot"><option value="">Global chantier</option>{lots.map((lot)=><option key={lot.id} value={lot.id}>{lot.code} · {lot.nom}</option>)}</select></label>
            <Field name="unite" label="Unité" required />
            <Field name="quantite" label="Quantité" type="number" min="0.0001" step="0.0001" required />
            {capabilities.costs && <Field name="cout_unitaire" label="Coût unitaire" type="number" min="0" defaultValue="0" />}
            <Field name="mouvement_stock_reference" label="Référence mouvement stock" />
            <Area name="notes" label="Notes" />
          </>}
          <div className="flex justify-end gap-2 md:col-span-2">
            <button type="button" className="btn-secondary" onClick={() => setCreateKind(null)}>Annuler</button>
            <button className="btn-primary">Enregistrer</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function Field(props: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  min?: string | number;
  max?: string | number;
  step?: string;
  defaultValue?: string;
  placeholder?: string;
}) {
  const { label, ...inputProps } = props;
  return <label><span className="label">{label}</span><input className="field" {...inputProps} /></label>;
}

function Area({ name, label, required = false }: { name: string; label: string; required?: boolean }) {
  return <label className="md:col-span-2"><span className="label">{label}</span><textarea className="field min-h-24" name={name} required={required} /></label>;
}

function Select({ name, label, options }: { name: string; label: string; options: [string, string][] }) {
  return <label><span className="label">{label}</span><select className="field" name={name}>{options.map(([value, text])=><option key={value} value={value}>{text}</option>)}</select></label>;
}
